import React, { FC, useState } from 'react';
import type {
  OrganisationMember,
  OrganisationGroup,
  OrganisationCardPolicy,
  OrganisationCardPolicyOverride,
  SetOrganisationCardPolicyOverrideRequest,
} from '@dreamai/types';
import { capLabel, parseOptionalMoney } from './organisationCardLabels';

export type CardOverrideTarget =
  | { kind: 'group'; group: OrganisationGroup }
  | { kind: 'member'; member: OrganisationMember };

interface OrganisationCardOverridesProps {
  policy: OrganisationCardPolicy;
  overrides: OrganisationCardPolicyOverride[];
  groups: OrganisationGroup[];
  busyKey: string | null;
  onSave: (
    target: CardOverrideTarget,
    values: SetOrganisationCardPolicyOverrideRequest,
  ) => Promise<boolean>;
  onClear: (target: CardOverrideTarget) => Promise<boolean>;
}

type EnabledChoice = 'inherit' | 'yes' | 'no';

interface OverrideFormValues {
  enabled: EnabledChoice;
  perTransactionCap: string;
  cycleCap: string;
}

function formFor(
  override: OrganisationCardPolicyOverride | undefined,
): OverrideFormValues {
  return {
    enabled:
      override?.cardEnabled == null
        ? 'inherit'
        : override.cardEnabled
          ? 'yes'
          : 'no',
    perTransactionCap:
      override?.perTransactionCap == null
        ? ''
        : String(override.perTransactionCap),
    cycleCap: override?.cycleCap == null ? '' : String(override.cycleCap),
  };
}

/** Whether purchases end up allowed for this row, given the organisation default. */
function purchasesAllowed(
  enabled: EnabledChoice,
  organisationEnabled: boolean,
): boolean {
  return enabled === 'yes' || (enabled === 'inherit' && organisationEnabled);
}

function validate(
  values: OverrideFormValues,
  organisationEnabled: boolean,
): { error: string } | { request: SetOrganisationCardPolicyOverrideRequest } {
  const cardEnabled =
    values.enabled === 'inherit' ? null : values.enabled === 'yes';
  if (!purchasesAllowed(values.enabled, organisationEnabled)) {
    return {
      request: { cardEnabled, perTransactionCap: null, cycleCap: null },
    };
  }
  const perTransactionCap = parseOptionalMoney(values.perTransactionCap);
  const cycleCap = parseOptionalMoney(values.cycleCap);
  if (perTransactionCap === undefined || cycleCap === undefined) {
    return { error: 'Limits must be amounts of zero or more, or left blank.' };
  }
  return { request: { cardEnabled, perTransactionCap, cycleCap } };
}

function describeOverride(
  override: OrganisationCardPolicyOverride | undefined,
  currency: string,
  organisationEnabled: boolean,
): string {
  if (!override) return 'Uses the organisation policy';
  if (override.cardEnabled === false) return 'Purchases off';
  const parts: string[] = [];
  if (override.cardEnabled === true) parts.push('Purchases on');
  if (override.cardEnabled === null && !organisationEnabled) {
    return 'Uses the organisation policy';
  }
  if (override.perTransactionCap !== null) {
    parts.push(
      `${capLabel(override.perTransactionCap, currency)} per purchase`,
    );
  }
  if (override.cycleCap !== null) {
    parts.push(`${capLabel(override.cycleCap, currency)} a month`);
  }
  return parts.length ? parts.join(' · ') : 'Uses the organisation policy';
}

interface OverrideRowProps {
  id: string;
  name: string;
  meta?: string;
  currency: string;
  organisationEnabled: boolean;
  override: OrganisationCardPolicyOverride | undefined;
  busy: boolean;
  onSave: (
    values: SetOrganisationCardPolicyOverrideRequest,
  ) => Promise<boolean>;
  onClear: () => Promise<boolean>;
}

const OverrideRow: FC<OverrideRowProps> = ({
  id,
  name,
  meta,
  currency,
  organisationEnabled,
  override,
  busy,
  onSave,
  onClear,
}) => {
  const [editing, setEditing] = useState(false);
  const [values, setValues] = useState<OverrideFormValues>(() =>
    formFor(override),
  );
  const [error, setError] = useState<string | null>(null);

  const startEditing = () => {
    setValues(formFor(override));
    setError(null);
    setEditing(true);
  };

  const showCaps = purchasesAllowed(values.enabled, organisationEnabled);

  const submit = async () => {
    const result = validate(values, organisationEnabled);
    if ('error' in result) {
      setError(result.error);
      return;
    }
    setError(null);
    const { cardEnabled, perTransactionCap, cycleCap } = result.request;
    const clears =
      cardEnabled === null && perTransactionCap === null && cycleCap === null;
    const ok = clears ? await onClear() : await onSave(result.request);
    if (ok) setEditing(false);
  };

  return (
    <li className='organisation-group-item' data-testid={`card-override-${id}`}>
      <div className='organisation-group-row'>
        <div className='organisation-group-info'>
          <span className='organisation-group-name'>{name}</span>
          <span className='organisation-group-meta'>
            {meta ? `${meta} · ` : ''}
            {describeOverride(override, currency, organisationEnabled)}
          </span>
        </div>
        {!editing && (
          <div className='organisation-group-actions'>
            <button
              type='button'
              className='btn-secondary'
              disabled={busy}
              onClick={startEditing}
            >
              {override ? 'Edit' : 'Change'}
            </button>
            {override && (
              <button
                type='button'
                className='btn-secondary'
                disabled={busy}
                onClick={onClear}
              >
                Clear
              </button>
            )}
          </div>
        )}
      </div>

      {editing && (
        <div className='organisation-group-form'>
          <div className='organisation-field-grid'>
            <label className='organisation-field'>
              <span className='field-label'>Purchases</span>
              <select
                className='field-select'
                value={values.enabled}
                disabled={busy}
                onChange={(e) =>
                  setValues({
                    ...values,
                    enabled: e.target.value as EnabledChoice,
                  })
                }
              >
                <option value='inherit'>
                  Same as organisation ({organisationEnabled ? 'on' : 'off'})
                </option>
                <option value='yes'>On</option>
                <option value='no'>Off</option>
              </select>
            </label>
            {showCaps && (
              <>
                <label className='organisation-field'>
                  <span className='field-label'>Per purchase ({currency})</span>
                  <input
                    type='number'
                    className='field-input'
                    min={0}
                    step='0.01'
                    placeholder='Same as organisation'
                    value={values.perTransactionCap}
                    disabled={busy}
                    onChange={(e) =>
                      setValues({
                        ...values,
                        perTransactionCap: e.target.value,
                      })
                    }
                  />
                </label>
                <label className='organisation-field'>
                  <span className='field-label'>Per month ({currency})</span>
                  <input
                    type='number'
                    className='field-input'
                    min={0}
                    step='0.01'
                    placeholder='Same as organisation'
                    value={values.cycleCap}
                    disabled={busy}
                    onChange={(e) =>
                      setValues({ ...values, cycleCap: e.target.value })
                    }
                  />
                </label>
              </>
            )}
          </div>
          {error && (
            <p className='organisation-feedback error' role='alert'>
              {error}
            </p>
          )}
          <div className='organisation-actions'>
            <button
              type='button'
              className='btn-primary'
              disabled={busy}
              onClick={submit}
            >
              {busy ? 'Saving…' : 'Save'}
            </button>
            <button
              type='button'
              className='btn-secondary'
              disabled={busy}
              onClick={() => setEditing(false)}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </li>
  );
};

/**
 * Per-group card limits, shown only while Groups are on for the organisation.
 * A member in several groups gets the most generous group value; a member's
 * own limits (set from the Team cards card) win over any group.
 */
const OrganisationCardOverrides: FC<OrganisationCardOverridesProps> = ({
  policy,
  overrides,
  groups,
  busyKey,
  onSave,
  onClear,
}) => {
  const byGroup = new Map(
    overrides.filter((o) => o.groupId).map((o) => [o.groupId!, o]),
  );

  return (
    <div
      className='organisation-card'
      data-testid='organisation-card-overrides'
    >
      <h3 className='organisation-card-title'>Purchases by group</h3>
      <p className='organisation-card-text'>
        Turn purchases on or off, or set different limits, for a group. A member
        in several groups gets the most generous group value; a member&rsquo;s
        own limits win over their groups.
        {policy.cardEnabled ? (
          <>
            {' '}
            Blank fields use the organisation limits (
            {capLabel(policy.perTransactionCap, policy.currency)} per purchase,{' '}
            {capLabel(policy.cycleCap, policy.currency)} a month).
          </>
        ) : (
          ' Purchases are off for the organisation, so only groups and members switched on here can buy anything.'
        )}
      </p>

      <details
        className='organisation-details'
        data-testid='organisation-card-groups-toggle'
      >
        <summary>
          Groups ({groups.length}){' '}
          {byGroup.size > 0 && (
            <span className='organisation-stat-sub'>
              · {byGroup.size} with their own settings
            </span>
          )}
        </summary>
        {groups.length === 0 ? (
          <p className='organisation-empty'>
            No groups yet — create one under Groups to set limits for a team.
          </p>
        ) : (
          <ul className='organisation-group-list'>
            {groups.map((group) => (
              <OverrideRow
                key={group.id}
                id={`group-${group.id}`}
                name={group.name}
                meta={`${group.membershipIds.length} ${group.membershipIds.length === 1 ? 'member' : 'members'}`}
                currency={policy.currency}
                organisationEnabled={policy.cardEnabled}
                override={byGroup.get(group.id)}
                busy={busyKey === `card:group:${group.id}`}
                onSave={(values) => onSave({ kind: 'group', group }, values)}
                onClear={() => onClear({ kind: 'group', group })}
              />
            ))}
          </ul>
        )}
      </details>
    </div>
  );
};

export default OrganisationCardOverrides;
